const ScrollLock = (() => {
  /* Lock by hiding overflow only. The old `position: fixed; top: 0` snapped
     the page to the very top whenever the search opened, and un-stuck the
     sticky header out from under the fixed search band. Overflow keeps the
     page exactly where it is (the body carries scrollbar-gutter: stable, so
     the vanishing scrollbar causes no layout shift). */
  /* iOS Safari ignores overflow: hidden for touch scrolling, so a swipe on
     the search band, or past either end of the results list, scrolled the
     page behind the search instead. While locked, a swipe is only let
     through when it starts inside a scrollable part of the search that can
     still move in that direction. */
  let touchStartX = 0;
  let touchStartY = 0;

  /* Any vertically scrollable box under the finger counts, so the results
     list and panels opened from it (quick add, cart drawer) keep scrolling. */
  function scrollableAncestor(target) {
    for (let el = target instanceof Element ? target : null; el && el !== document.body; el = el.parentElement) {
      if (el.scrollHeight > el.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
        return el;
      }
    }
    return null;
  }

  function onTouchStart(event) {
    touchStartX = event.touches[0]?.clientX || 0;
    touchStartY = event.touches[0]?.clientY || 0;
  }

  function onTouchMove(event) {
    if (event.touches.length > 1) return;
    const deltaX = (event.touches[0]?.clientX || 0) - touchStartX;
    const deltaY = (event.touches[0]?.clientY || 0) - touchStartY;
    // Sideways swipes (image sliders, swatch rows) can't move the page.
    if (Math.abs(deltaX) > Math.abs(deltaY)) return;
    const scroller = scrollableAncestor(event.target);
    if (scroller) {
      const atTop = scroller.scrollTop <= 0;
      const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;
      if (!(deltaY > 0 && atTop) && !(deltaY < 0 && atBottom)) return;
    }
    if (event.cancelable) event.preventDefault();
  }

  let locked = false;

  function lock() {
    document.body.style.overflow = "hidden";
    if (locked) return;
    locked = true;
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
  }

  function unlock() {
    document.body.style.overflow = "";
    if (!locked) return;
    locked = false;
    document.removeEventListener("touchstart", onTouchStart);
    document.removeEventListener("touchmove", onTouchMove);
  }

  function cleanupIfFixed() {
    unlock();
  }

  return { lock, unlock, cleanupIfFixed };
})();

/* A media query instead of window.innerWidth, as in header-navigation.js: on phones (meta viewport)
   Chrome brings style and layout of the whole page up to date for every innerWidth read. The panel
   read it at every typing pause, every tap on the field, and right after each new set of results
   was inserted, so the phone laid out the new cards there and again after the loader was cleared.
   Same breakpoint as before (750px and narrower), and like innerWidth it counts the scrollbar. */
const predictiveSearchPhoneQuery = window.matchMedia("(max-width: 750px)");
const isPredictiveSearchPhoneWidth = () => predictiveSearchPhoneQuery.matches;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    // A Map, not an object: a typed term such as "constructor" was read from Object.prototype.
    this.cachedResults = new Map();
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.scrollY = 0;
    this.loaderOverlay = null;

    this.setupEventListeners();
    // Ensure scroll is unlocked when the component initializes
    this.ensureScrollUnlocked();
    this._enterHandler = this._enterHandler?.bind(this);
  }

  connectedCallback() {
    super.connectedCallback?.();
    ["keydown"].forEach((t) => window.addEventListener(t, this._handleEnterKey, true));
  }

  disconnectedCallback() {
    ["keydown"].forEach((t) => window.removeEventListener(t, this._handleEnterKey, true));
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.handleViewportResize);
      window.visualViewport.removeEventListener("scroll", this.handleViewportResize);
    } else {
      window.removeEventListener("resize", this.handleViewportResize);
    }
    super.disconnectedCallback?.();
  }

  ensureScrollUnlocked() {
    ScrollLock.cleanupIfFixed();
  }

  createSearchLoader() {
    // Check if loader exists in DOM (it might have been removed when innerHTML was replaced)
    if (this.loaderOverlay && !document.body.contains(this.loaderOverlay)) {
      this.loaderOverlay = null;
    }
    
    if (!this.loaderOverlay) {
      const searchContainer = this.querySelector(".predictive-search.predictive-search--header");
      if (searchContainer) {
        this.loaderOverlay = document.createElement("div");
        this.loaderOverlay.className = "predictive-search-loader-overlay";
        this.loaderOverlay.innerHTML = `<span class="loader"></span>`;
        searchContainer.appendChild(this.loaderOverlay);
      }
    }
    return this.loaderOverlay;
  }

  showLoader() {
    const searchContainer = this.querySelector(".predictive-search.predictive-search--header");
    const loader = this.createSearchLoader();
    if (loader && searchContainer) {
      searchContainer.scrollTop = 0;
      searchContainer.classList.add("search-loading");
      loader.classList.add("show");
    }
  }

  hideLoader() {
    if (this.loaderOverlay && document.body.contains(this.loaderOverlay)) {
      this.loaderOverlay.classList.remove("show");
    }
    const searchContainer = this.querySelector(".predictive-search.predictive-search--header");
    if (searchContainer) {
      searchContainer.classList.remove("search-loading");
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.input.addEventListener("click", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    document.querySelector(".search-modal__close-button")?.addEventListener("click", this.onModalClose.bind(this));
    document.querySelector(".search-modal__clear-button")?.addEventListener("click", this.onFormReset.bind(this));
    // Remove lockScroll from search icon click - only lock scroll when results are actually displayed
    document.querySelector(".header__icon--search")?.addEventListener("click", this.onSearchIconClick.bind(this));
   
    this.handleViewportResize = this.handleViewportResize.bind(this);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.handleViewportResize);
      window.visualViewport.addEventListener("scroll", this.handleViewportResize);
    } else {
      window.addEventListener("resize", this.handleViewportResize);
    }
  }

  onSearchIconClick() {
    // Surface the default suggestions as soon as the panel opens, rather than
    // waiting for a second interaction with the input. Deferred a frame so it
    // runs after details-modal finishes its own open sequence, which would
    // otherwise close the results again. Focusing the input is what a shopper
    // would do next anyway, and onFocus() already handles the empty-term case
    // (clears stale results, opens, reveals the default block).
    this.onFormReset();
    // details-modal runs its own open sequence after this handler and resets
    // the results, so the reveal is queued behind it. The component already
    // uses short timeouts for its mobile sizing for the same reason.
    setTimeout(() => {
      if (this.getQuery().length) return;
      this.input?.focus();
      this.onFocus();
    }, 100);
  }

  // Shopify reads one trailing "*" as "starts with", but two or more ("lipstick**") broke the query and
  // returned unrelated products (false lashes first). Repeated asterisks count as one.
  normalizeQuery(value) {
    return String(value || "").replace(/\*{2,}/g, "*").trim();
  }

  getQuery() {
    return this.normalizeQuery(this.input.value);
  }

  // The term as it is sent to Shopify (the panel request and the results page): lowercase. Shopify's
  // search is only partly case-insensitive: products with the words match in any case, but the loose
  // extra matches and the order change with the case ("BRONZE FILTER PALETTE" listed 13 products,
  // "bronze filter palette" 19 and "Liquid Matte" 49), so the same words gave a different list.
  getRequestQuery() {
    return this.getQuery().toLowerCase();
  }

  lockScroll() {
    ScrollLock.lock();
  }

  unlockScroll() {
    ScrollLock.unlock();
  }

  onModalClose() {
    // Only the search modal's own <details>: other open ones on the page (cart loyalty card,
    // accordions, filters) must stay as the shopper left them.
    const ownDetails = this.closest("details[open]");
    const openDetailsElement = ownDetails ? [ownDetails] : [];
    openDetailsElement.forEach((element) => {
      const summaryElement = element.querySelector("summary");
      element.removeAttribute("open");
      summaryElement.setAttribute("aria-expanded", false);
      summaryElement.focus();
    });
    this.searchTerm = "";
    this.abortController.abort();
    this.abortController = new AbortController();
    this.hideLoader();
    this.onChange(true);
    document.querySelector(".search-modal__clear-button")?.classList.add("hide");
    this.querySelector(".no-results-search")?.classList.add("hide");
    this.querySelector(".predictive-search__result-group")?.remove();
    this.querySelector(".predictive-search__search-for-button")?.remove();
    document.body.classList.remove("overflow-hidden");
    this.unlockScroll();
    this.closeResults(true);
  }

  onChange() {
    super.onChange();
    if (isPredictiveSearchPhoneWidth()) {
      const predictiveSearchContainer = this.querySelector(".predictive-search");
      if (predictiveSearchContainer) {
        this.applyMobileSheetSize(predictiveSearchContainer);
      }
    } else {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    const newSearchTerm = this.getQuery();
    
    // Abort any pending requests when search term changes significantly
    if (!newSearchTerm.length || !this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.abortController.abort();
      this.abortController = new AbortController();
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector(".predictive-search__result-group")?.remove();
      this.querySelector(".predictive-search__search-for-button")?.remove();
      this.hideLoader();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // No search term, ensure scroll is unlocked and show default content
      this.hideLoader(); 
      this.unlockScroll();
      // Ensure any result groups are removed (in case async response came after abort)
      this.querySelector(".predictive-search__result-group")?.remove();
      this.querySelector(".predictive-search__search-for-button")?.remove();
      this.querySelector(".default-search-result")?.classList.remove("hide");
      document.querySelector(".search-modal__clear-button")?.classList.add("hide");
      this.querySelector(".no-results-search")?.classList.add("hide");
      this.removeAttribute("results");
      if (document.activeElement === this.input) {
        /* Backspacing to empty keeps the panel open on the default
           suggestions; it used to close and only reopen on the next click. */
        this.removeAttribute("loading");
        this.setAttribute("open", true);
        this.isOpen = true;
      } else {
        // Close any open results to prevent scroll locking
        this.closeResults(true);
      }
      return;
    }

    document.querySelector(".search-modal__clear-button")?.classList.remove("hide");

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const isQuickAddButton = event?.submitter?.classList?.value?.includes("color-swatch-item");

    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a') || isQuickAddButton) {
      event.preventDefault();
      return;
    }
    // The results page gets the same cleaned, lowercase term as the panel.
    if (this.input.value !== this.getRequestQuery()) this.input.value = this.getRequestQuery();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.hideLoader();
      // Unlock scroll first before calling onChange
      this.unlockScroll();
      this.onChange();
      document.querySelector(".search-modal__clear-button")?.classList.add("hide");
      this.querySelector(".no-results-search")?.classList.add("hide");
      this.closeResults(true);
      // Clearing with the button moves focus to the button, so onChange() saw
      // the input as blurred and closed the panel; the default suggestions
      // only came back on the next click into the field. Hand focus back and
      // reopen on the default block, as backspacing to empty already does.
      if (this.closest("details")?.hasAttribute("open")) {
        this.input.focus();
        this.onFocus();
      }
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // Ensure any stale results are removed before showing default content
      this.querySelector(".predictive-search__result-group")?.remove();
      this.querySelector(".predictive-search__search-for-button")?.remove();
      this.removeAttribute("results");
      this.searchTerm = "";
      this.open();
      this.querySelector(".default-search-result")?.classList.remove("hide");
       if (isPredictiveSearchPhoneWidth()) {
        setTimeout(() => this.handleViewportResize(), 300);
      }
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
      if (isPredictiveSearchPhoneWidth()) {
        setTimeout(() => this.handleViewportResize(), 300);
      }
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const activeElement = document.activeElement;
      const isCartDrawer = activeElement?.closest("cart-drawer") || activeElement?.closest("#CartDrawer");
      const cartDrawer = document.querySelector("cart-drawer");
      const isCartDrawerOpen = cartDrawer?.classList.contains("active") || cartDrawer?.isOpen;
      const hasSearchTerm = this.searchTerm && this.searchTerm.length > 0;
      const detailsElement = this.closest("details");
      const isDetailsOpen = detailsElement?.hasAttribute("open");
     
      if (!this.contains(activeElement) && !isCartDrawer && !isCartDrawerOpen && !hasSearchTerm && !isDetailsOpen) {
        this.close();
      }
    });
  }

  _handleEnterKey = (e) => {
    if (!this.isOpen) return;

    const within = this.contains(e.target) || e.target.closest("predictive-search") === this;
    if (!within || e.target !== this.input || e.isComposing) return;

    const isEnter = e.key === "Enter" || e.code === "Enter" || e.code === "NumpadEnter" || e.keyCode === 13;
    if (!isEnter) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    const q = (this.getQuery?.() || this.input?.value || "").trim();
    if (!q) return;

    const form = this.input?.form;
    if (typeof form?.requestSubmit === "function") form.requestSubmit();
    else form?.submit?.();
  };

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      // The term is literal text: unescaped, "lip (matte" threw here (so no search ran) and "." or
      // "*" matched other characters.
      const previousTermPattern = new RegExp(previousTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
      if (currentButtonText.match(previousTermPattern)?.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      // A replacer function, so "$&" or "$'" in the typed term is inserted as typed.
      const newButtonText = currentButtonText.replace(previousTerm, () => newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  getSearchResults(searchTerm) {
    // Keyed on the exact q that is sent: the old key replaced only the first space, so "red lip" and
    // "red-lip" shared one cached response (and its "no results" line quoted the other spelling).
    // q is the lowercase term (see getRequestQuery), so "LIP" and "lip" are one request.
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    const cachedMarkup = this.cachedResults.get(queryKey);
    if (cachedMarkup) {
      this.renderSearchResults(cachedMarkup);
      return;
    }

    // The same term is already being fetched by the current controller (a tap or refocus while it
    // loads): let it finish instead of cancelling it and asking the server to render it again.
    if (
      this.inflight &&
      this.inflight.term === searchTerm &&
      this.inflight.signal === this.abortController.signal &&
      !this.inflight.signal.aborted
    ) {
      return;
    }

    // Only the newest term's request is useful: a request still running for an earlier prefix
    // ("lip" while "lipstick" is typed) is cancelled rather than left to render every card on
    // the server and parse on the main thread. Every other abort (close, clear, a non-prefix
    // edit) swaps in a new controller and resets the loader itself.
    this.abortController.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.inflight = { term: searchTerm, signal };

    this.showLoader();

    // Up to 10 of each resource type. Shopify's default is 10 shared by suggestions, collections,
    // pages and products, so a word that also matches collections ("bronze") left three or four
    // products, and fewer once VIP-only ones were skipped: new launches were missing from the panel.
    // Arabic only (data-search-all-text is rendered for Arabic): Shopify's predictive search has no
    // Arabic, so /search/suggest answers 417 "Unsupported buyer locale" and the panel listed no
    // products. The search page renders the same section from its product results instead.
    const requestUrl = this.dataset.searchAllText
      ? `${this.input.form.getAttribute("action")}?q=${encodeURIComponent(queryKey)}&type=product&options%5Bprefix%5D=last&section_id=predictive-search`
      : `${routes.predictive_search_url}?q=${encodeURIComponent(queryKey)}&section_id=predictive-search&resources[limit]=10&resources[limit_scope]=each`;
    fetch(requestUrl, {
      signal,
    })
      .then((response) => {
        // Arabic only: if the search page fails too, the panel keeps its suggestions and offers the
        // full results page instead of closing.
        if (!response.ok && this.dataset.searchAllText) {
          if (searchTerm === this.searchTerm) {
            this.hideLoader();
            this.renderSearchAllFallback();
          } else if (signal === this.abortController.signal) {
            this.hideLoader();
          }
          return null;
        }
        if (!response.ok) {
          var error = new Error(response.status);
          this.hideLoader();
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (text === null) return;
        const resultsMarkup = this.extractSectionMarkup(text);
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults.set(queryKey, resultsMarkup);
        });
        // Skip rendering if the user has already typed a different term. The loader belongs to
        // the request that is still current, so a stale one leaves it alone.
        if (searchTerm !== this.searchTerm) {
          if (signal === this.abortController.signal) this.hideLoader();
          return;
        }
        // The loader is cleared inside the render (see renderSearchResults), not after it.
        this.renderSearchResults(resultsMarkup, true);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Aborted. Every abort site swaps in a new controller and handles the loader itself, so a
          // request rejected here has always been superseded and leaves the loader and the loading
          // state to whatever replaced it. The signal check only matters for an abort that keeps
          // the current controller, which no code path does today.
          if (signal === this.abortController.signal) {
            this.hideLoader();
            this.removeAttribute("loading");
          }
          return;
        }
        this.hideLoader();
        this.close();
        throw error;
      })
      .finally(() => {
        if (this.inflight?.signal === signal) this.inflight = null;
      });
  }

  // The Section Rendering response is the section wrapper alone, so its inner markup can be sliced
  // out of the text and parsed once, by the innerHTML assignment in renderSearchResults, instead of
  // parsing the whole response with DOMParser and serialising it back first. Any other shape falls
  // back to the DOMParser path.
  extractSectionMarkup(text) {
    const openTag = '<div id="shopify-section-predictive-search"';
    const openStart = text.indexOf(openTag);
    const openEnd = openStart === -1 ? -1 : text.indexOf(">", openStart);
    const closeStart = text.lastIndexOf("</div>");
    if (
      openStart !== -1 &&
      openEnd !== -1 &&
      closeStart > openEnd &&
      text.slice(0, openStart).trim() === "" &&
      text.slice(closeStart + 6).trim() === ""
    ) {
      return text.slice(openEnd + 1, closeStart);
    }
    return new DOMParser().parseFromString(text, "text/html").querySelector("#shopify-section-predictive-search")
      .innerHTML;
  }

  // Arabic fallback for a failed panel request: the default suggestions stay visible with a button
  // that submits the form to the full search results page.
  renderSearchAllFallback() {
    this.querySelector(".predictive-search__result-group")?.remove();
    this.querySelector(".predictive-search__search-for-button")?.remove();
    this.querySelector(".no-results-search")?.classList.add("hide");
    this.querySelector(".default-search-result")?.classList.remove("hide");

    const wrapper = document.createElement("div");
    wrapper.id = "predictive-search-option-search-keywords";
    wrapper.className = "predictive-search__search-for-button";
    const button = document.createElement("button");
    button.className =
      "predictive-search__item predictive-search__item--term see-all-results-button button button--full-width button--primary";
    const label = document.createElement("span");
    label.textContent = this.dataset.searchAllText;
    button.appendChild(label);
    wrapper.appendChild(button);
    this.predictiveSearchResults.appendChild(wrapper);

    this.removeAttribute("loading");
    this.setAttribute("results", true);
    this.open();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.textContent = "";
    }, 1000);
  }

  // clearLoader: true for a fetched response, whose loader used to be cleared right after this ran.
  renderSearchResults(resultsMarkup, clearLoader = false) {
    // Don't render results if search term has been cleared
    if (!this.searchTerm || !this.searchTerm.length) {
      this.hideLoader();
      return;
    }
    
    // Pre-calculate and set height for mobile before inserting HTML to prevent flash. The sheet's
    // top edge does not move with its content, so this height is also the one open() applies below
    // (passed on instead of measured again, which laid out the new cards inside this task).
    let mobileHeight;
    if (isPredictiveSearchPhoneWidth()) {
      const predictiveSearchContainer = this.querySelector(".predictive-search");
      if (predictiveSearchContainer) {
        mobileHeight = this.getMobileContainerHeight();
        predictiveSearchContainer.style.height = `${mobileHeight}px`;
        predictiveSearchContainer.style.maxHeight = `calc(100dvh - 120px)`;
      }
    }
    // Same task, nothing paints in between: cleared after the sheet was measured (the layout is
    // still clean there) and before the new cards go in. Cleared after the render, it lifted the
    // panel's overflow lock once the new cards had been laid out, so they were laid out again.
    if (clearLoader) this.hideLoader();
   
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("open", true);
    this.isOpen = true;
    this.removeAttribute("loading");
    this.setAttribute("results", true);
    this.dispatchEvent(
      new CustomEvent("predictive-search:results-updated", {
        bubbles: true,
        detail: {
          searchTerm: this.searchTerm,
        },
      }),
    );

    const hasRenderableResults =
      this.querySelector(".predictive-search__result-group") ||
      this.querySelector(".predictive-search__search-for-button");

    const isExplicitEmpty =
      this.querySelector(".no-results-search") || this.querySelector('[data-predictive-search-empty="true"]');
    if (hasRenderableResults) {
      document.querySelectorAll(".predictive-search .featured-card-product .product-form__submit").forEach((btn) => {
        btn.addEventListener(
          "click",
          () => {
            const currentSearchTerm = this.searchTerm;
            document.addEventListener(
              "cart:predictive-add",
              () => {
                if (currentSearchTerm && currentSearchTerm.length > 0) {
                  if (this.input.value !== currentSearchTerm) {
                    this.input.value = currentSearchTerm;
                    this.searchTerm = currentSearchTerm;
                  }
                  if (!this.isOpen) {
                    this.open();
                  }
                }
              },
              { once: true },
            );
          },
          { once: true },
        );
      });
      this.setLiveRegionResults();
      this.open(mobileHeight);
    } else {
      const liveText =
        this.querySelector("[data-predictive-search-live-region-count-value]")?.textContent?.trim() || "";
      if (liveText) this.setLiveRegionText(liveText);
      this.unlockScroll();
      this.setAttribute("results", false);
      this.classList.toggle("predictive-search--empty", !!isExplicitEmpty);
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const text = this.querySelector("[data-predictive-search-live-region-count-value]")?.textContent || "";
    this.setLiveRegionText(text);
  }

  getResultsMaxHeight() {
    /* End the panel well above the viewport's bottom edge (10% breathing
       room, at least 48px). The old formula subtracted the bottom of
       ".section-header", which no longer matches an element, so the result
       was NaN and the panel ran uncapped to the bottom of the screen. */
    const panelTop = this.predictiveSearchResults?.getBoundingClientRect().top || 0;
    const bottomGap = Math.max(32, Math.round(window.innerHeight * 0.06));
    this.resultsMaxHeight = Math.max(200, window.innerHeight - panelTop - bottomGap);
    return this.resultsMaxHeight;
  }
 
  applyMobileSheetSize(predictiveSearchContainer, knownHeight) {
    /* The full-height sheet exists so a results grid can scroll; the
       pre-typing keyword bubbles are a few rows, and filling the viewport
       under them left a large empty white sheet. knownHeight: the
       getMobileContainerHeight() value the caller already measured in this
       task (renderSearchResults), so it is not measured again. */
    const showsCompactDefault =
      !this.getQuery().length && this.querySelector(".default-search-result .predictive-search__keywords");
    if (showsCompactDefault) {
      predictiveSearchContainer.style.height = "auto";
    } else {
      predictiveSearchContainer.style.height = `${knownHeight ?? this.getMobileContainerHeight()}px`;
    }
    predictiveSearchContainer.style.maxHeight = `calc(100dvh - 120px)`;
  }

  getMobileContainerHeight() {
    /* Bottom of what's visible, in the same coordinates as
       getBoundingClientRect(). When the iOS keyboard opens Safari pans the
       page, so the visible area starts offsetTop px down; leaving that out
       sized the sheet past the keyboard and its last results were
       unreachable. */
    const viewport = window.visualViewport;
    const viewportHeight = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;

    /* Fill everything from the sheet's own top edge to the bottom of the
       screen. Fall back to the input when the sheet isn't measurable yet
       (12px is the gap between the bar and the sheet). */
    let sheetTop = 0;
    const container = this.querySelector(".predictive-search");
    if (container) {
      sheetTop = container.getBoundingClientRect().top;
    }
    if (!sheetTop || sheetTop <= 0) {
      const inputBottom = this.input?.getBoundingClientRect().bottom || 0;
      sheetTop = inputBottom + 12;
    }

    return Math.max(200, Math.round(viewportHeight - sheetTop));
  }
 
  handleViewportResize() {
    if (this.isOpen && this.getAttribute("open") === "true" && isPredictiveSearchPhoneWidth()) {
      const predictiveSearchContainer = this.querySelector(".predictive-search");
      if (predictiveSearchContainer) {
        if (predictiveSearchContainer.style.height === "auto") {
          this.applyMobileSheetSize(predictiveSearchContainer);
        } else {
          const newHeight = this.getMobileContainerHeight();
          const currentHeight = parseInt(predictiveSearchContainer.style.height) || newHeight;
          if (Math.abs(newHeight - currentHeight) > 10) {
            this.applyMobileSheetSize(predictiveSearchContainer);
          }
        }
      }
    }
  }

  shouldLockScroll() {
    // Only lock scroll if we have actual search results AND the search is open
    return (
      this.getAttribute("results") === "true" && this.getAttribute("open") === "true" && this.searchTerm.length > 0
    );
  }

  // knownSheetHeight: optional, the phone sheet height renderSearchResults measured before inserting
  // the results (see applyMobileSheetSize). The re-check 100 ms later always measures.
  open(knownSheetHeight) {
    this.setAttribute("open", true);
    this.lockScroll();

    if (isPredictiveSearchPhoneWidth()) {
      const predictiveSearchContainer = this.querySelector(".predictive-search");
      if (predictiveSearchContainer) {
        this.applyMobileSheetSize(predictiveSearchContainer, knownSheetHeight);
        setTimeout(() => {
          this.applyMobileSheetSize(predictiveSearchContainer);
        }, 100);
      }
    } else {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
   
    this.isOpen = true;

    document.getElementById("stickyPanel")?.classList?.remove("visible");
    document.getElementsByClassName("sticky-atc-mobile")[0]?.classList?.remove("visible");
  }

  close(clearSearchTerm = false) {
    this.hideLoader();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    const details = this.closest("details");

    if (!details?.open) {
      this.unlockScroll();
    }

    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      // Ensure scroll is unlocked when clearing search
      this.unlockScroll();
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.resultsMaxHeight = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
